/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import MapView from '../Multimodal/components/Mapview/MapView'
import Header from '../Multimodal/components/Headers/Header'
import config from '../Multimodal/config/config'
import CriteriaSelection from '../Multimodal/components/CriteriaSelection/CriteriaSelection'
import ScoringSummary from '../Multimodal/components/ScoringSummary/ScoringSummary'
import SplashScreen from '../Multimodal/components/SplashScreen'
import { onNewStreetDefaultClick } from '../streets/creation'
import {
  updateStreetBasedOnTravelLane,
  updateStreetWidth
} from '../Multimodal/utils/multimodal.utils'
import {
  resetRoadwayVariables,
  setRoadwayVariables
} from '../Multimodal/slices/generalInputs/roadwayVariables'
import {
  resetModalPrioritization,
  setAllModalPrioritization
} from '../Multimodal/slices/generalInputs/modalPrioritization'
import {
  resetModalTiers,
  setAllModalTiers
} from '../Multimodal/slices/generalInputs/modalTiers'
import {
  LAND_USE_CONTEXT,
  TRAVEL_LANES
} from '../Multimodal/constants/roadwayVariables'
import { removeAllSegments } from '../segments/remove'
import { updateSettings } from '../store/slices/settings'
import { NEW_STREET_EMPTY } from '../streets/constants'
import { resetPedestrianInputs } from '../Multimodal/slices/criteriaSelection/pedestrian'
import { resetFreightInputs } from '../Multimodal/slices/criteriaSelection/freight'
import { resetAutoInputs } from '../Multimodal/slices/criteriaSelection/auto'
import { resetBicycleInputs } from '../Multimodal/slices/criteriaSelection/bicycle'
import {
  clearAllHCTAmenities,
  resetTransitInputs
} from '../Multimodal/slices/criteriaSelection/transit'
import ReportPage from '../Multimodal/components/Report/ReportPage'
import SubHeader from '../Multimodal/components/Headers/SubHeader'
import {
  MAIN_STREET,
  RURAL,
  SUBURBAN_CORRIDORS,
  URBAN_OR_WALKABLE_THOROUGHFARE
} from '../Multimodal/config/segments.config'
import { PEDESTRIAN } from '../Multimodal/constants/modalTiers'
import { BICYCLE } from '../Multimodal/constants/modalTiers'
import { TRANSIT } from '../Multimodal/constants/modalTiers'
import { AUTO } from '../Multimodal/constants/modalTiers'
import { FREIGHT } from '../Multimodal/constants/modalTiers'
import {
  resetPrioritiesDictionary,
  setPrioritiesDictionary
} from '../Multimodal/slices/generalInputs/modalPrioritiesCardDict'
import {
  resetDefaultModalTiers,
  setDefaultModalTiers,
  setManualStreetMode,
  setReportAutoImgUrl,
  setReportBicycleImgUrl,
  setReportFreightImgUrl,
  setReportHeaderDataUrl,
  setReportPedestrialImgUrl,
  setReportTickImgDataUrl,
  setReportTransitImgUrl,
  setSelectedFeature,
  setShowPopup,
  setShowToolbox
} from '../Multimodal/slices/appSlice'
import { setActiveTool } from '../Multimodal/slices/segmentSelectionToolbox'
import { remixStreet } from '../streets/remix'
import { ADD } from '../Multimodal/constants/segmentSelectionToolbox'
import {
  setDrawerOpen,
  setProjectInputStep
} from '../Multimodal/slices/mainTool'
import {
  AUTO_TIER,
  BIKE_TIER,
  FREIGHT_TIER,
  LANDUSE_CONTEXT,
  PED_TIER,
  ST_NAME,
  ST_TYPE,
  TRANSIT_TIER
} from '../Multimodal/constants/layerFieldNames'
const multimodalLogo = new URL(
  '../../images/illustrations/SEMCOG-MultiTool-Logo.png',
  import.meta.url
)
const tickImg = new URL(
  '../../images/illustrations/objective-met.png',
  import.meta.url
)
const pedestrianImg = new URL(
  '../../images/illustrations/pedestrian.png',
  import.meta.url
)
const transitImg = new URL(
  '../../images/illustrations/transit.png',
  import.meta.url
)
const autoImg = new URL('../../images/illustrations/auto.png', import.meta.url)
const freightImg = new URL(
  '../../images/illustrations/freight.png',
  import.meta.url
)
const bicycleImg = new URL(
  '../../images/illustrations/bicycle.png',
  import.meta.url
)

const theme = createTheme({
  palette: {
    primary: {
      main: config.themeConfig.primaryColor
    },
    text: {
      primary: config.themeConfig.semcogBlue1,
      secondary: config.themeConfig.lightTextColor,
      heading: config.themeConfig.titleTextColor
    }
  },
  typography: {
    h2: {
      fontSize: config.themeConfig.headerFontSize,
      fontWeight: 'bold'
    },
    body1: {
      fontSize: config.themeConfig.textFontSize,
      color: config.themeConfig.primaryTextColor
    },
    h3: {
      fontSize: config.themeConfig.textFontSize,
      fontWeight: 600
    }
  }
})

function App() {
  const { activeStep, drawerOpen } = useSelector((state) => state.mainTool)

  const {
    selectedFeature,
    shareableURL,
    selectedFeaturesArr,
    manualStreetMode
  } = useSelector((state) => state.appSlice)
  const { width, name, segments } = useSelector((state) => state.street)
  const {
    Pedestrian: PTier,
    Bicycle: BTier,
    Transit: TTier,
    Auto: ATier,
    Freight: FTier
  } = useSelector((state) => state.modalTiers)
  const { modalPrioritization, modalTiers } = useSelector((state) => state)
  const { prioritiesDictionary } = useSelector(
    (state) => state.modalPrioritiesCardDict
  )
  const hctValue = useSelector(
    (state) => state.transit.high_capacity_transit_facility
  )
  const { street_width, land_use_context, travel_lanes } = useSelector(
    (state) => state.roadwayVariables
  )
  const [showModal, setShowModal] = useState(null)
  const dispatch = useDispatch()

  useEffect(() => {
    const showModalValue = localStorage.getItem('showSplashScreen')
      ? JSON.parse(localStorage.getItem('showSplashScreen'))
      : true
    setShowModal(shareableURL ? false : showModalValue)

    // When app mounts, reset to empty street
    removeAllSegments()
    dispatch(
      updateSettings({
        newStreetPreference: NEW_STREET_EMPTY
      })
    )

    // make all images used in report as urls , to prevent breaking from parcel imports
    urlToDataURL(multimodalLogo).then((result) => {
      dispatch(setReportHeaderDataUrl(result))
    })
    // report tick img
    urlToDataURL(tickImg).then((result) => {
      dispatch(setReportTickImgDataUrl(result))
    })
    urlToDataURL(pedestrianImg).then((result) => {
      dispatch(setReportPedestrialImgUrl(result))
    })
    urlToDataURL(autoImg).then((result) => {
      dispatch(setReportAutoImgUrl(result))
    })
    urlToDataURL(bicycleImg).then((result) => {
      dispatch(setReportBicycleImgUrl(result))
    })
    urlToDataURL(freightImg).then((result) => {
      dispatch(setReportFreightImgUrl(result))
    })
    urlToDataURL(transitImg).then((result) => {
      dispatch(setReportTransitImgUrl(result))
    })
  }, [])

  async function urlToDataURL(url) {
    const response = await fetch(url)
    const blob = await response.blob()

    return await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result) // Base64 string
      reader.readAsDataURL(blob)
    })
  }

  // construct street when travel lane changes
  useEffect(() => {
    !shareableURL &&
      land_use_context &&
      updateStreetBasedOnTravelLane(land_use_context)
  }, [travel_lanes])

  useEffect(() => {
    // based on land use content, set travel lanes
    // do not update travel lanes based on land use context when user is in manual mode
    const totalTravelLanesInCurrentSt = segments.filter((segment) =>
      ['drive-lane', 'bus-lane', 'brt-lane'].includes(segment.type)
    ).length
    !shareableURL &&
      !manualStreetMode &&
      dispatch(
        setRoadwayVariables({
          field: TRAVEL_LANES,
          value: totalTravelLanesInCurrentSt
        })
      )
  }, [land_use_context, manualStreetMode])

  useEffect(() => {
    // If shareable URL is false, then remix street
    if (!shareableURL) {
      remixStreet()
    }
  }, [shareableURL])

  useEffect(() => {
    // If shareable URL and active step changes to 1 , show panel
    if (
      (shareableURL && selectedFeature) ||
      (shareableURL && manualStreetMode)
    ) {
      dispatch(setDrawerOpen(true))
    }
  }, [shareableURL, selectedFeature, manualStreetMode])

  // When selectedFeaturesArr changes, set the selectedFeature input
  useEffect(() => {
    if (selectedFeaturesArr.length && !selectedFeature) {
      dispatch(setSelectedFeature(selectedFeaturesArr[0]))
    }
    // When selected features arr is empty , clear the selected Feature as well
    if (!selectedFeaturesArr.length && selectedFeature !== null) {
      dispatch(setSelectedFeature(null))
    }

    // When arr is empty and selectedfeature is null . Set active tool to ADD agian to allow user to begin adding segements
    if (!selectedFeaturesArr.length && selectedFeature === null) {
      dispatch(setActiveTool(ADD))
    }
  }, [selectedFeaturesArr, selectedFeature])

  // When active step is not equal to 0, reset the toolbox tool to ADD
  useEffect(() => {
    if (activeStep !== 0) {
      dispatch(setActiveTool(ADD))
      // Hide toolbox
      dispatch(setShowToolbox(false))
    } else if (activeStep === 0) {
      dispatch(setShowPopup(false))
    }

    // update width to match the street width
    if (activeStep === 1) {
      updateStreetWidth(street_width)
    }
  }, [activeStep])

  useEffect(() => {
    // Whenever HCT Facility value changes, reset the HCT Amenities checkbox values in redux
    dispatch(clearAllHCTAmenities())
  }, [hctValue])

  // update the priorities dictionary cards in project input three - when modalPrioritization changes
  useEffect(() => {
    if (Object.values(modalPrioritization).every((val) => val)) {
      dispatch(
        setPrioritiesDictionary({
          field: 'prioritiesDictionary',
          value: Object.values(modalPrioritization).map((priority) =>
            prioritiesDictionary.find((obj) => obj.tier === priority)
          )
        })
      )
    }
  }, [modalPrioritization])

  useEffect(() => {
    if (drawerOpen) {
      // close popup
      dispatch(setShowPopup(false))
      // close toolbox
      dispatch(setShowToolbox(false))
    } else if (drawerOpen === false && selectedFeature) {
      dispatch(setShowPopup(true))
      // close toolbox
      dispatch(setShowToolbox(true))
    }
  }, [drawerOpen, selectedFeature])

  useEffect(() => {
    // Update Priorities Dictionary when modalTiers are changed
    let dictCopy = [...prioritiesDictionary]
    dictCopy = dictCopy.map((obj) => {
      return { ...obj, modalTier: modalTiers[obj.tier] }
    })
    dispatch(
      setPrioritiesDictionary({
        field: 'prioritiesDictionary',
        value: dictCopy
      })
    )
  }, [modalTiers])

  useEffect(() => {
    // Do not reset all inputs when shareable url is present

    if (selectedFeature && !shareableURL) {
      // if  selectedFeature exsist , then set manual mode to false
      dispatch(setManualStreetMode(false))
      // Reset all criteria inputs, when selected Feature changes
      dispatch(resetPedestrianInputs())
      dispatch(resetFreightInputs())
      dispatch(resetAutoInputs())
      dispatch(resetBicycleInputs())
      dispatch(resetTransitInputs())

      // Reset all control panel inputs, whenever new feature is selected
      dispatch(resetRoadwayVariables())
      // dispatch(resetModalPrioritization())
      // dispatch(resetModalTiers())

      dispatch(
        setRoadwayVariables({
          field: 'roadway_name',
          value:
            selectedFeature.attributes[ST_NAME] +
            ' ' +
            selectedFeature.attributes[ST_TYPE]
        })
      )
      dispatch(
        setRoadwayVariables({
          field: LAND_USE_CONTEXT,
          value: selectedFeature.attributes[LANDUSE_CONTEXT]
        })
      )

      // Once a feature is selected setup all the tiers
      dispatch(
        setAllModalTiers({
          Pedestrian:
            selectedFeature.attributes[PED_TIER] === null
              ? 0
              : selectedFeature.attributes[PED_TIER],
          Bicycle:
            selectedFeature.attributes[BIKE_TIER] === null
              ? 0
              : selectedFeature.attributes[BIKE_TIER],
          Transit:
            selectedFeature.attributes[TRANSIT_TIER] === null
              ? 0
              : selectedFeature.attributes[TRANSIT_TIER],
          Auto:
            selectedFeature.attributes[AUTO_TIER] === null
              ? 0
              : selectedFeature.attributes[AUTO_TIER],
          Freight:
            selectedFeature.attributes[FREIGHT_TIER] === null
              ? 0
              : selectedFeature.attributes[FREIGHT_TIER]
        })
      )

      // Once a feature is selected setup DEFAULT MODAL TIERS as well
      dispatch(
        setDefaultModalTiers({
          Pedestrian:
            selectedFeature.attributes[PED_TIER] === null
              ? 0
              : selectedFeature.attributes[PED_TIER],
          Bicycle:
            selectedFeature.attributes[BIKE_TIER] === null
              ? 0
              : selectedFeature.attributes[BIKE_TIER],
          Transit:
            selectedFeature.attributes[TRANSIT_TIER] === null
              ? 0
              : selectedFeature.attributes[TRANSIT_TIER],
          Auto:
            selectedFeature.attributes[AUTO_TIER] === null
              ? 0
              : selectedFeature.attributes[AUTO_TIER],
          Freight:
            selectedFeature.attributes[FREIGHT_TIER] === null
              ? 0
              : selectedFeature.attributes[FREIGHT_TIER]
        })
      )

      onNewStreetDefaultClick(selectedFeature.attributes[LANDUSE_CONTEXT])
    }
  }, [selectedFeature])

  // useEffect(() => {
  //   if (street_width) {
  //     updateStreetWidth(street_width)
  //   }
  // }, [street_width])

  useEffect(() => {
    // when street width changes, update the width of roadway variables
    // if user is in manual street mode, do not update street width in project inputs
    if (selectedFeature?.attributes?.[ST_NAME]) {
      dispatch(setRoadwayVariables({ field: 'street_width', value: width }))
    } else if (manualStreetMode) {
      // else if manual street mode is true, update the width of roadway variables
      // dispatch(setRoadwayVariables({ field: 'street_width', value: width }))
    }
  }, [width, manualStreetMode])

  useEffect(() => {
    // when street width changes, update the width of roadway variables
    if (selectedFeature?.attributes?.[ST_NAME]) {
      dispatch(setRoadwayVariables({ field: 'street_width', value: width }))
    }
  }, [selectedFeature?.attributes?.[ST_NAME]])

  useEffect(() => {
    if (manualStreetMode && !shareableURL) {
      dispatch(resetRoadwayVariables())
      dispatch(resetModalTiers())
      dispatch(resetDefaultModalTiers())
      dispatch(resetModalPrioritization())
      dispatch(resetPrioritiesDictionary())

      dispatch(setRoadwayVariables({ field: 'street_width', value: '' }))
      dispatch(setProjectInputStep(0))
      dispatch(setDrawerOpen(true))
      // remix street
      remixStreet()
      // manually set road name to empty string
      dispatch(setRoadwayVariables({ field: 'roadway_name', value: '' }))
    }
  }, [manualStreetMode, shareableURL])

  useEffect(() => {
    // when street name changes, update the name of roadway variables
    name &&
      !manualStreetMode &&
      dispatch(setRoadwayVariables({ field: 'roadway_name', value: name }))
  }, [name, manualStreetMode])

  // Update priorities when modal tiers change
  useEffect(() => {
    let modalPrioirtyArr = Array(5)
    let tier = 1
    let PTierCopy = PTier
    let BTierCopy = BTier
    let TTierCopy = TTier
    let ATierCopy = ATier
    let FTierCopy = FTier
    let defaultPriorities = []

    switch (land_use_context) {
      case MAIN_STREET:
        // Set deafult priorites based on land use context
        defaultPriorities = [
          'Pedestrian',
          'Bicycle',
          'Transit',
          'Auto',
          'Freight'
        ]
        for (let i = 0; i <= 4; i++) {
          while (modalPrioirtyArr[i] === undefined) {
            if (PTierCopy === tier) {
              modalPrioirtyArr[i] = PEDESTRIAN
              PTierCopy = 0
            } else if (BTierCopy === tier) {
              modalPrioirtyArr[i] = BICYCLE
              BTierCopy = 0
            } else if (TTierCopy === tier) {
              modalPrioirtyArr[i] = TRANSIT
              TTierCopy = 0
            } else if (ATierCopy === tier) {
              modalPrioirtyArr[i] = AUTO
              ATierCopy = 0
            } else if (FTierCopy === tier) {
              modalPrioirtyArr[i] = FREIGHT
              FTierCopy = 0
            } else if (tier > 4) {
              modalPrioirtyArr[i] = 'N/A'
            } else {
              tier++
            }
          }
        }
        break
      case URBAN_OR_WALKABLE_THOROUGHFARE:
        // Set deafult priorites based on land use context
        defaultPriorities = [
          'Transit',
          'Pedestrian',
          'Bicycle',
          'Auto',
          'Freight'
        ]
        for (let i = 0; i <= 4; i++) {
          while (modalPrioirtyArr[i] === undefined) {
            if (TTierCopy === tier) {
              modalPrioirtyArr[i] = TRANSIT
              TTierCopy = 0
            } else if (PTierCopy === tier) {
              modalPrioirtyArr[i] = PEDESTRIAN
              PTierCopy = 0
            } else if (BTierCopy === tier) {
              modalPrioirtyArr[i] = BICYCLE
              BTierCopy = 0
            } else if (ATierCopy === tier) {
              modalPrioirtyArr[i] = AUTO
              ATierCopy = 0
            } else if (FTierCopy === tier) {
              modalPrioirtyArr[i] = FREIGHT
              FTierCopy = 0
            } else if (tier > 4) {
              modalPrioirtyArr[i] = 'N/A'
            } else {
              tier++
            }
          }
        }
        break
      case SUBURBAN_CORRIDORS:
        // Set deafult priorites based on land use context
        defaultPriorities = [
          'Auto',
          'Pedestrian',
          'Bicycle',
          'Transit',
          'Freight'
        ]
        for (let i = 0; i <= 4; i++) {
          while (modalPrioirtyArr[i] === undefined) {
            if (ATierCopy === tier) {
              modalPrioirtyArr[i] = AUTO
              ATierCopy = 0
            } else if (PTierCopy === tier) {
              modalPrioirtyArr[i] = PEDESTRIAN
              PTierCopy = 0
            } else if (BTierCopy === tier) {
              modalPrioirtyArr[i] = BICYCLE
              BTierCopy = 0
            } else if (TTierCopy === tier) {
              modalPrioirtyArr[i] = TRANSIT
              TTierCopy = 0
            } else if (FTierCopy === tier) {
              modalPrioirtyArr[i] = FREIGHT
              FTierCopy = 0
            } else if (tier > 4) {
              modalPrioirtyArr[i] = 'N/A'
            } else {
              tier++
            }
          }
        }
        break
      case RURAL:
        // Set deafult priorites based on land use context
        defaultPriorities = [
          'Auto',
          'Freight',
          'Bicycle',
          'Pedestrian',
          'Transit'
        ]
        for (let i = 0; i <= 4; i++) {
          while (modalPrioirtyArr[i] === undefined) {
            if (ATierCopy === tier) {
              modalPrioirtyArr[i] = AUTO
              ATierCopy = 0
            } else if (FTierCopy === tier) {
              modalPrioirtyArr[i] = FREIGHT
              FTierCopy = 0
            } else if (BTierCopy === tier) {
              modalPrioirtyArr[i] = BICYCLE
              BTierCopy = 0
            } else if (PTierCopy === tier) {
              modalPrioirtyArr[i] = PEDESTRIAN
              PTierCopy = 0
            } else if (TTierCopy === tier) {
              modalPrioirtyArr[i] = TRANSIT
              TTierCopy = 0
            } else if (tier > 4) {
              modalPrioirtyArr[i] = 'N/A'
            } else {
              tier++
            }
          }
        }
        break
      default:
    }

    // const defaultPriorityArr = [
    //   selectedFeature.attributes.Priority_1,
    //   selectedFeature.attributes.Priority_2,
    //   selectedFeature.attributes.Priority_3,
    //   selectedFeature.attributes.Priority_4,
    //   selectedFeature.attributes.Priority_5
    // ]

    // Since some of the priorities are missing or N/A
    // We will have to filter and add the missing priorities to the end of priority array

    const na_indexArr = []
    const resultArr = []
    modalPrioirtyArr.forEach((priority, i) => {
      defaultPriorities = defaultPriorities.filter((name) => name !== priority)
      priority === 'N/A' && na_indexArr.push(i)
      if (priority !== 'N/A') {
        resultArr.push({
          name: priority,
          index: i
        })
      }
    })
    if (resultArr.length !== 5) {
      defaultPriorities.forEach((name, i) => {
        resultArr.push({
          name,
          index: na_indexArr[i]
        })
      })
    }
    // Once a feature is selected setup all the priorities
    dispatch(
      setAllModalPrioritization({
        Priority_1: resultArr[0]?.name,
        Priority_2: resultArr[1]?.name,
        Priority_3: resultArr[2]?.name,
        Priority_4: resultArr[3]?.name,
        Priority_5: resultArr[4]?.name
      })
    )
  }, [PTier, BTier, TTier, ATier, FTier, land_use_context])

  return (
    <>
      <ThemeProvider theme={theme}>
        <Header />
        {activeStep === 3 && <SubHeader />}
        {activeStep === 0 && <MapView />}
        {/* {activeStep === 1 && <GeneralInputs />} */}
        {activeStep === 1 && <CriteriaSelection />}
        {activeStep === 2 && <ScoringSummary />}
        {activeStep === 3 && <ReportPage />}
        {showModal && <SplashScreen />}
      </ThemeProvider>
    </>
  )
}

export default App
